import { cur } from '@/lib/currency';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Receipt, ArrowRight, Calendar, Loader2, AlertCircle } from 'lucide-react';
import api from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { toLocalYmd } from '@/lib/dateRange';

const fmtSR = (n) => `${cur()} ${(Number(n) || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

/**
 * iter434 — VAT Liability mini-dashboard.
 *
 * Shows current VAT Output / Input / Settlements totals for the selected
 * period and the resulting net VAT Payable. One-click "Record VAT
 * Settlement" jumps to /expenses with VAT Settlement category pre-filled
 * for the suggested next-period amount.
 *
 * iter435 — `compact` prop: tighter spacing, smaller fonts, no
 * recent-settlements list — designed to fit alongside other Dashboard
 * widgets without dominating the viewport.
 */
export function VATLiabilityWidget({ compact = false }) {
  const navigate = useNavigate();
  const today = new Date();
  const firstDay = toLocalYmd(new Date(today.getFullYear(), today.getMonth(), 1));
  const lastDay = toLocalYmd(new Date(today.getFullYear(), today.getMonth() + 1, 0));

  const [start, setStart] = useState(firstDay);
  const [end, setEnd] = useState(lastDay);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/accounting/vat-liability-summary', {
        params: { period_start: start, period_end: end },
      });
      setData(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [start, end]);

  const recordSettlement = () => {
    if (!data) return;
    const params = new URLSearchParams({
      preset: 'vat_settlement',
      amount: String(data.vat_payable_total || 0),
      description: `VAT Settlement — ${data.period?.start} → ${data.period?.end}`,
    });
    navigate(`/expenses?${params.toString()}`);
  };

  const owed = (data?.vat_payable_total || 0) > 0;

  return (
    <Card className="border-purple-200/60 dark:border-purple-900/40" data-testid="vat-liability-widget">
      <CardHeader className={`${compact ? 'pb-1.5 pt-3' : 'pb-2'} flex flex-row items-center justify-between gap-3`}>
        <CardTitle className={`${compact ? 'text-sm' : 'text-base'} flex items-center gap-2`}>
          <Receipt size={compact ? 12 : 14} className="text-purple-600" />
          VAT Liability
          {loading && <Loader2 size={11} className="animate-spin text-muted-foreground" />}
        </CardTitle>
        {!compact && (
          <div className="flex items-center gap-1.5">
            <Calendar size={12} className="text-muted-foreground" />
            <Input type="date" value={start} onChange={e => setStart(e.target.value)}
              className="h-7 w-32 text-xs" data-testid="vat-period-start" />
            <span className="text-xs text-muted-foreground">→</span>
            <Input type="date" value={end} onChange={e => setEnd(e.target.value)}
              className="h-7 w-32 text-xs" data-testid="vat-period-end" />
          </div>
        )}
        {compact && (
          <span className="text-[10px] text-muted-foreground" data-testid="vat-period-label">
            {(data?.period?.start || start)?.slice(5)} → {(data?.period?.end || end)?.slice(5)}
          </span>
        )}
      </CardHeader>
      <CardContent className={compact ? 'pb-3' : ''}>
        <div className={`grid ${compact ? 'grid-cols-2 gap-2' : 'grid-cols-2 sm:grid-cols-4 gap-3'}`}>
          <div className={`rounded-lg bg-muted/40 ${compact ? 'p-2' : 'p-3'}`} data-testid="vat-output">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">VAT collected</div>
            <div className={`${compact ? 'text-sm' : 'text-base'} font-bold text-success mt-0.5`}>{fmtSR(data?.vat_output_total)}</div>
          </div>
          <div className={`rounded-lg bg-muted/40 ${compact ? 'p-2' : 'p-3'}`} data-testid="vat-input">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">VAT paid</div>
            <div className={`${compact ? 'text-sm' : 'text-base'} font-bold text-info mt-0.5`}>{fmtSR(data?.vat_input_total)}</div>
          </div>
          {!compact && (
            <div className="rounded-lg bg-muted/40 p-3" data-testid="vat-settlements">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Settled to ZATCA</div>
              <div className="text-base font-bold text-purple-700 mt-0.5">{fmtSR(data?.vat_settlements_total)}</div>
            </div>
          )}
          <div className={`rounded-lg ${compact ? 'p-2 col-span-2' : 'p-3'} ${owed ? 'bg-warning/10 border border-warning/40' : 'bg-success/10 border border-success/40'}`} data-testid="vat-payable">
            <div className="text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1">
              {owed && <AlertCircle size={11} className="text-warning" />}
              Net VAT Payable
            </div>
            <div className={`${compact ? 'text-base' : 'text-lg'} font-bold mt-0.5 ${owed ? 'text-warning' : 'text-success'}`}>
              {fmtSR(data?.vat_payable_total)}
            </div>
          </div>
        </div>

        {!compact && data?.recent_settlements?.length > 0 && (
          <div className="mt-3 rounded-md border border-border bg-muted/20" data-testid="vat-recent-settlements">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold px-3 py-1.5 border-b">
              Recent settlements
            </div>
            <div className="divide-y divide-border">
              {data.recent_settlements.map(s => (
                <div key={s.id} className="px-3 py-1.5 flex justify-between items-center text-xs">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-muted-foreground font-mono shrink-0">{(s.date || '').slice(0, 10)}</span>
                    <span className="truncate">{s.description}</span>
                  </div>
                  <span className="font-semibold text-purple-700 ml-2 shrink-0">{fmtSR(s.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={`${compact ? 'mt-2' : 'mt-3'} flex items-center justify-between gap-3 flex-wrap`}>
          <p className="text-xs text-muted-foreground">
            Next filing: <span className="font-semibold text-foreground">{data?.next_period?.label || '—'}</span>
          </p>
          <Button
            size="sm"
            onClick={recordSettlement}
            disabled={!data || (data.vat_payable_total || 0) <= 0}
            className={`bg-purple-600 hover:bg-purple-700 text-white ${compact ? 'h-7 text-xs px-2' : 'h-8'}`}
            data-testid="vat-record-settlement-btn"
          >
            Record Settlement <ArrowRight size={12} className="ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
